import { useState } from 'react';
import type { RotateAccessCodeResponse } from './types';
import { sanitizeDisplayMessage } from '../../lib/adminApi';

interface RotateAccessCodeResultProps {
  result: RotateAccessCodeResponse;
  onBack: () => void;
  clinicName?: string;
  clinicSlug?: string;
  onViewLaunch?: () => void;
  recoveryMessage?: string;
}

/**
 * One-time reveal panel for a rotated demo access code (U4). Mirrors the
 * checkout-only branch of OneTimeLinks.tsx, but for a single raw access code
 * instead of a URL — the raw code is a bearer credential and must never be
 * persisted, logged, or left in React state after confirmed dismissal
 * (R14/R15).
 */
export function RotateAccessCodeResult({
  result,
  onBack,
  clinicName,
  clinicSlug,
  onViewLaunch,
  recoveryMessage,
}: RotateAccessCodeResultProps) {
  const [copyError, setCopyError] = useState<string | null>(null);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const hasAccessCode = Boolean(result.demo_access_code);

  const copyToClipboard = async () => {
    if (!result.demo_access_code) return;
    try {
      await navigator.clipboard.writeText(result.demo_access_code);
      setCopyError(null);
    } catch {
      // Never include the raw value in the fallback copy or log it — this
      // carries a bearer credential (demo access code) and must not leak via
      // console/error text (R14).
      setCopyError('Copy failed. Select the value manually and copy it.');
    }
  };

  const handleDismiss = () => {
    setShowDismissConfirm(false);
    onBack();
  };

  return (
    <div className="one-time-panel" data-testid="rotate-access-code-result">
      <h3>{hasAccessCode ? 'Access code rotated' : 'Access code rotation unavailable'}</h3>
      {hasAccessCode && (
        <p className="one-time-warning">
          This access code is shown once and is not stored. Copy it now.
        </p>
      )}

      {(clinicName || clinicSlug) && (
        <div className="form-row">
          <label htmlFor="rotate-clinic-name">Clinic</label>
          <div className="link-field">
            <input id="rotate-clinic-name" readOnly value={clinicName || clinicSlug} data-testid="rotate-clinic-name" />
          </div>
        </div>
      )}

      <div className="form-row">
        <label htmlFor="rotate-launch-id">Launch ID</label>
        <div className="link-field">
          <input id="rotate-launch-id" readOnly value={result.launch_id} data-testid="rotate-launch-id" />
        </div>
      </div>

      {hasAccessCode ? (
        <div className="form-row" data-testid="rotate-access-code-row">
          <label htmlFor="rotate-access-code">New demo access code</label>
          <div className="link-field">
            <input id="rotate-access-code" readOnly value={result.demo_access_code} data-testid="rotate-access-code" />
            <button
              className="btn btn--secondary"
              onClick={copyToClipboard}
              data-testid="copy-rotate-access-code"
            >
              Copy
            </button>
          </div>
          <p className="one-time-warning">
            The old access code no longer works for future logins. This new code cannot be
            recovered after you leave this screen. If it is lost, an intentional rotate is
            required to generate another one.
          </p>
          {copyError && <div className="copy-fallback">{copyError}</div>}
        </div>
      ) : (
        <div className="copy-fallback" data-testid="rotate-access-code-missing">
          {recoveryMessage
            ? sanitizeDisplayMessage(recoveryMessage)
            : 'No new access code was returned. This can happen on a timeout, an idempotency replay, or a conflict. Durable detail has been refreshed — rotate again intentionally if a new code is still needed. Do not treat any previous code as still valid.'}
        </div>
      )}

      <div className="actions">
        {onViewLaunch && (
          <button
            className="btn btn--secondary"
            onClick={onViewLaunch}
            data-testid="view-rotate-launch-button"
          >
            View originating launch
          </button>
        )}
        {hasAccessCode ? (
          !showDismissConfirm ? (
            <button
              className="btn btn--danger"
              onClick={() => setShowDismissConfirm(true)}
              data-testid="dismiss-rotate-button"
            >
              Dismiss and clear access code
            </button>
          ) : (
            <div className="dismiss-confirm" data-testid="dismiss-rotate-confirm">
              <p>The new access code cannot be recovered after dismissal. Are you sure?</p>
              <button className="btn btn--danger" onClick={handleDismiss} data-testid="confirm-dismiss-rotate">
                Yes, dismiss
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => setShowDismissConfirm(false)}
                data-testid="cancel-dismiss-rotate"
              >
                Cancel
              </button>
            </div>
          )
        ) : (
          <button className="btn" onClick={onBack} data-testid="rotate-back">
            Back to launch detail
          </button>
        )}
      </div>
    </div>
  );
}
